import { tr } from "@/i18n";

// Backend errors are Italian `detail` strings. Known user-facing ones are mapped to translation keys here
// (no backend contract change); unknown ones fall back to the raw text.
const KNOWN: [RegExp, string][] = [
  [/^Sessione scaduta/i, "errors.sessionExpired"],
  [/^Non autenticato|^Accesso richiesto/i, "errors.unauthenticated"],
  [/^Sessione non valida|^Token non valido/i, "errors.sessionExpired"],
  [/^Email o password non (validi|corretti)/i, "errors.invalidCredentials"],
  [/^Email già registrata|^Esiste già un account/i, "errors.emailTaken"],
  [/^Email non valida/i, "errors.invalidEmail"],
  [/^La password deve/i, "errors.weakPassword"],
  [/^Progetto non trovato/i, "errors.projectNotFound"],
  [/^Scena non trovata/i, "errors.sceneNotFound"],
  [/^Clip non trovata/i, "errors.clipNotFound"],
  [/^Lavoro non trovato/i, "errors.jobNotFound"],
  [/^Montage non trovato/i, "errors.montageNotFound"],
  [/^Operazione in corso/i, "errors.operationInProgress"],
  [/^Questo lavoro è ancora in elaborazione/i, "errors.jobStillProcessing"],
  [/^Il Correttore V1 supporta clip fino a 15 secondi/i, "errors.clipTooLong"],
  [/^Il Correttore V1 supporta clip di almeno 3 secondi/i, "errors.clipTooShort"],
  [/^Il file supera il limite di 200 MB/i, "errors.fileTooLarge"],
  [/^Formato non supportato.*(JPE?G|PNG|WEBP)/i, "errors.unsupportedImageFormat"],
  [/^Formato non supportato/i, "errors.unsupportedFormat"],
  [/^Immagine troppo grande/i, "errors.imageTooLarge"],
  [/^Il file non è un video/i, "errors.notAVideo"],
  [/^Il file non contiene una traccia video/i, "errors.noVideoStream"],
  [/^Risoluzione non supportata/i, "errors.unsupportedResolution"],
  [/^Descrivi cosa non va/i, "errors.promptRequired"],
  [/^Esegui prima l'analisi/i, "errors.analyzeFirst"],
  [/^Correzione già completata/i, "errors.correctionDone"],
  [/^Lavoro completato: usa/i, "errors.correctionDone"],
  [/^Il provider non ha potuto elaborare questo contenuto/i, "errors.contentPolicy"],
  [/^La correzione AI non è riuscita/i, "errors.correctionFailed"],
  [/^Video corretto generato ma salvataggio non riuscito/i, "errors.correctionStorage"],
  [/^Il provider sta ancora elaborando/i, "errors.providerStillProcessing"],
  [/^Analisi non riuscita/i, "errors.analysisFailed"],
  [/^Analisi AI non configurata|^Correzione AI non configurata/i, "errors.aiNotConfigured"],
  [/^Pulizia dei file non riuscita/i, "errors.cleanupFailed"],
  [/^Il video (corretto )?non è (ancora )?disponibile/i, "errors.videoUnavailable"],
  [/^Caricamento non riuscito/i, "errors.uploadFailed"],
  [/^Chunk mancanti/i, "errors.uploadIncomplete"],
  [/^Lingua non supportata/i, "errors.unsupportedLanguage"],
  [/^Troppi tentativi/i, "errors.tooManyAttempts"],
  [/^Limite (giornaliero|orario|di generazioni)/i, "errors.generationCap"],
  [/^Attendi .* prima di|^Troppe generazioni ravvicinate/i, "errors.cooldown"],
  [/^Immagine non valida|^Il file non è un'immagine/i, "errors.invalidImage"],
  [/^Il rendering è già in corso|^Render già in corso|^Render in corso|^Un render è già in corso/i, "errors.renderInProgress"],
  [/^Render interrotto/i, "errors.renderInterrupted"],
  [/^Nessuna clip pronta da montare/i, "errors.noReadyClips"],
  [/^Troppe preparazioni in corso/i, "errors.tooManyPreparations"],
  [/^Inserisci un URL/i, "errors.urlRequired"],
  [/^Aggiungi almeno un video|^Il montage è vuoto/i, "errors.montageEmpty"],
  [/^Generazione avviata/i, "errors.directLocked"],
  [/^Devi confermare di avere almeno 18 anni/i, "legal.acceptRequired"],
  [/^Puoi allegare al massimo/i, "errors.tooManyReferences"],
  [/^Questa storia richiede più di/i, "errors.tooManyClips"],
  [/^Generazione video non configurata/i, "errors.aiNotConfigured"],
  [/^Nessuna clip da riprovare|^Questa clip non è in errore/i, "errors.nothingToRetry"],
  [/^Video non disponibile/i, "errors.videoUnavailable"],
  [/^File vuoto/i, "errors.invalidImage"],
  [/^Formato immagine non supportato/i, "music.unsupportedImage"],
  [/^Formato audio non supportato|^Traccia audio non valida/i, "music.unsupportedAudio"],
  [/^Audio troppo lungo/i, "music.audioTooLong"],
  [/^Traccia troppo grande/i, "music.audioTooLarge"],
  [/^Creazione video non riuscita/i, "music.failed"],
  [/^Elaborazione interrotta da un riavvio/i, "music.interrupted"],
  [/^Caricamento interrotto da un riavvio/i, "montageEditor.uploadInterrupted"],
  [/^Upload già chiuso/i, "montageEditor.uploadClosed"],
  [/^Video non valido/i, "montageEditor.invalidVideo"],
  [/^Il testo non può essere vuoto/i, "textOverlay.emptyText"],
  [/^Testo troppo lungo/i, "textOverlay.tooLong"],
  [/^Intervallo non valido/i, "textOverlay.invalidTiming"],
  [/^Aggiungi prima un video al Montage/i, "textOverlay.addVideoFirst"],
  [/^Testo non trovato/i, "textOverlay.notFound"],
];

const CODE_KEYS: Record<string, string> = {
  SUBSCRIPTION_REQUIRED: "billing.subscriptionRequired",
  INSUFFICIENT_CREDITS: "credits.insufficient",
};

export function translateError(detail: unknown, fallbackKey = "errors.generic"): string {
  if (typeof detail === "string" && detail.trim()) {
    for (const [re, key] of KNOWN) if (re.test(detail)) return tr(key);
    return detail;
  }
  const code = (detail as any)?.code;
  if (typeof code === "string" && CODE_KEYS[code]) return tr(CODE_KEYS[code]);
  return tr(fallbackKey);
}

export const errorFrom = (err: any, fallbackKey = "errors.generic"): string =>
  translateError(err?.response?.data?.detail, fallbackKey);
